import { useTranslation } from "react-i18next";
import { TbAlertTriangle, TbBrowser, TbDeviceDesktop } from "react-icons/tb";
import "./browserUnsupportedNotice.scss";

/** Best-effort name for the message ("Firefox can't read folders"). Falls back to a generic wording. Only Firefox
 *  and Safari need naming — every Chromium browser (Chrome, Edge, Opera, Vivaldi …) supports folder access. */
export function detectUnsupportedBrowser(ua: string = typeof navigator !== "undefined" ? navigator.userAgent : ""): "Firefox" | "Safari" | null {
    if (/firefox|fxios/i.test(ua)) return "Firefox";
    if (/safari/i.test(ua) && !/chrome|chromium|crios|edg|opr|android/i.test(ua)) return "Safari";
    if (/iphone|ipad|ipod/i.test(ua)) return "Safari"; // every browser on iOS is WebKit
    return null;
}

/** Shown in the browser method when this browser has no folder access (Firefox, Safari, …): says why, and
 *  offers the two ways forward — a supported browser, or the desktop app, which works with any browser. */
export function BrowserUnsupportedNotice({ onUseApp }: { onUseApp: () => void }) {
    const { t } = useTranslation();
    const name = detectUnsupportedBrowser();

    return (
        <section className="browser-unsupported" role="note">
            <h2 className="browser-unsupported__title">
                <TbAlertTriangle aria-hidden="true" />
                {name ? t("syncMethod.unsupported.titleNamed", { browser: name }) : t("syncMethod.unsupported.title")}
            </h2>
            <p className="browser-unsupported__text">{t("syncMethod.unsupported.text")}</p>

            <ul className="browser-unsupported__options">
                <li>
                    <TbBrowser aria-hidden="true" />
                    <span>{t("syncMethod.unsupported.optionBrowser")}</span>
                </li>
                <li>
                    <TbDeviceDesktop aria-hidden="true" />
                    <span>{t("syncMethod.unsupported.optionApp")}</span>
                </li>
            </ul>

            <button type="button" className="btn-outline browser-unsupported__cta" onClick={onUseApp}>
                {t("syncMethod.unsupported.cta")}
            </button>

            <p className="browser-unsupported__hint">{t("syncMethod.unsupported.hint")}</p>
        </section>
    );
}
